import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import {
  formatReportCurrency,
  getStartAndEndDate,
  REPORT_DATE_FILTERS,
  REPORT_PAGINATION_LIMIT,
  REPORT_SKELETON_ROWS,
} from "../../../../../utils";
import AppPagination from "../../../../../Libraries/AppPagination";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const TopSellingItems = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, pageLoading, topSellingItems, topSellingItemsTotal } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDates = useRef(null);

  const fetchReport = useCallback((start, end, offset = 0) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "top_selling_items",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
      limit: REPORT_PAGINATION_LIMIT,
      offset,
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_top_selling_items_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("top_selling_items");

  const onFetch = useCallback((start, end) => {
    currentDates.current = { start, end };
    fetchReport(start, end, 0);
  }, [fetchReport]);

  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(onFetch, "top_selling_items");

  const handleNext = useCallback((offset) => {
    if (!currentDates.current) return;
    fetchReport(currentDates.current.start, currentDates.current.end, offset);
  }, [fetchReport]);

  const showNonIdeal = isYearlyFilter && !loading && topSellingItems.length === 0;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="top_selling_items_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.top_selling_items}
        id="top_selling_items"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="top_selling_items_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="top_selling_items_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "top_selling_items" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || topSellingItems.length > 0) && (
            <Box id="top_selling_items_table_header" className="invoiceListTableHeader">
              <Box id="top_selling_items_header_item_name" className="invListTableHeaderCol fourColumn textLeft">
                <ParagraphText id="top_selling_items_header_label_item_name" className="fontSize12 fontWeight500 newLightColor">{I18n.item_name}</ParagraphText>
              </Box>
              <Box id="top_selling_items_header_item_sold" className="invListTableHeaderCol fourColumn textLeft">
                <ParagraphText id="top_selling_items_header_label_item_sold" className="fontSize12 fontWeight500 newLightColor">{I18n.items_sold}</ParagraphText>
              </Box>
              <Box id="top_selling_items_header_gross_sales" className="invListTableHeaderCol fourColumn textRight">
                <ParagraphText id="top_selling_items_header_label_gross_sales" className="fontSize12 fontWeight500 newLightColor">{I18n.gross_sales}</ParagraphText>
              </Box>
              <Box id="top_selling_items_header_net_sales" className="invListTableHeaderCol fourColumn textRight">
                <ParagraphText id="top_selling_items_header_label_net_sales" className="fontSize12 fontWeight500 newLightColor">{I18n.net_sales}</ParagraphText>
              </Box>
            </Box>
          )}
          <Box id="topSellingScrollable" className={pageStyles.pageBody}>
            {loading ? (
              Array.from({ length: REPORT_SKELETON_ROWS }).map((_, i) => (
                <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={140} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <Skeleton variant="rounded" animation="wave" width={36} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              ))
            ) : topSellingItems.length === 0 ? (
              <FilterNonIdeal
                title={I18n.no_transactions_matching_those_filters}
                subTitle={`${I18n.try_adjusting_filters} `}
                subTitleText={I18n.view_yearly_data}
                subTitleTextOnClick={handleResetFilter}
                subTitleTextId="top_selling_items_filter_view_yearly"
              />
            ) : (
              <AppPagination
                data={{
                  handleNext,
                  pageLoad: pageLoading,
                  count: topSellingItemsTotal,
                  list: topSellingItems,
                  scrollableTarget: "topSellingScrollable",
                }}
              >
                {topSellingItems.map(({ item_name, item_sold, gross_sales, net_sales }, i) => (
                  <Box key={i} id={`top_selling_items_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                    <Box id={`top_selling_items_row_${i}_item_name`} className="invListTableHeaderCol fourColumn textLeft">
                      <ParagraphText id={`top_selling_items_cell_${i}_item_name`} className="fontSize14 newFontColor">{item_name}</ParagraphText>
                    </Box>
                    <Box id={`top_selling_items_row_${i}_item_sold`} className="invListTableHeaderCol fourColumn textLeft">
                      <ParagraphText id={`top_selling_items_cell_${i}_item_sold`} className="fontSize14 newFontColor">{item_sold}</ParagraphText>
                    </Box>
                    <Box id={`top_selling_items_row_${i}_gross_sales`} className="invListTableHeaderCol fourColumn textRight">
                      <ParagraphText id={`top_selling_items_cell_${i}_gross_sales`} className="fontSize14 newFontColor">{formatReportCurrency(gross_sales)}</ParagraphText>
                    </Box>
                    <Box id={`top_selling_items_row_${i}_net_sales`} className="invListTableHeaderCol fourColumn textRight">
                      <ParagraphText id={`top_selling_items_cell_${i}_net_sales`} className="fontSize14 newFontColor">{formatReportCurrency(net_sales)}</ParagraphText>
                    </Box>
                  </Box>
                ))}
              </AppPagination>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TopSellingItems;
